import { ref, onMounted, onUnmounted } from 'vue';
import { gsap } from 'gsap';

/**
 * Custom Cursor Composable
 * Creates a smooth-following custom cursor effect
 */
export function useCustomCursor() {
    const cursorDot = ref(null);
    const cursorOutline = ref(null);
    const isVisible = ref(false);

    let mouseX = 0;
    let mouseY = 0;
    let cursorX = 0;
    let cursorY = 0;

    const initCursor = () => {
        // Create cursor elements
        const dot = document.createElement('div');
        dot.className = 'custom-cursor-dot';
        document.body.appendChild(dot);
        cursorDot.value = dot;

        const outline = document.createElement('div');
        outline.className = 'custom-cursor-outline';
        document.body.appendChild(outline);
        cursorOutline.value = outline;

        // Mouse move handler
        const handleMouseMove = (e) => {
            mouseX = e.clientX;
            mouseY = e.clientY;

            if (!isVisible.value) {
                gsap.to([dot, outline], {
                    opacity: 1,
                    duration: 0.3
                });
                isVisible.value = true;
            }
        };

        // Mouse enter/leave handlers
        const handleMouseEnter = () => {
            gsap.to([dot, outline], {
                opacity: 1,
                duration: 0.3
            });
            isVisible.value = true;
        };

        const handleMouseLeave = () => {
            gsap.to([dot, outline], {
                opacity: 0,
                duration: 0.3
            });
            isVisible.value = false;
        };

        // Hover effect for interactive elements
        const handleHoverEnter = () => {
            gsap.to(outline, {
                scale: 2,
                duration: 0.3,
                ease: 'power2.out'
            });
            gsap.to(dot, {
                scale: 0.5,
                duration: 0.3,
                ease: 'power2.out'
            });
        };

        const handleHoverLeave = () => {
            gsap.to([outline, dot], {
                scale: 1,
                duration: 0.3,
                ease: 'power2.out'
            });
        };

        // Add event listeners
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseenter', handleMouseEnter);
        document.addEventListener('mouseleave', handleMouseLeave);

        // Add hover listeners to interactive elements
        const interactiveElements = document.querySelectorAll('a, button, .clickable');
        interactiveElements.forEach(el => {
            el.addEventListener('mouseenter', handleHoverEnter);
            el.addEventListener('mouseleave', handleHoverLeave);
        });

        // Smooth cursor animation loop
        const animate = () => {
            // Smooth follow effect using lerp (linear interpolation)
            cursorX += (mouseX - cursorX) * 0.15;
            cursorY += (mouseY - cursorY) * 0.15;

            gsap.set(dot, {
                x: mouseX,
                y: mouseY
            });

            gsap.set(outline, {
                x: cursorX,
                y: cursorY
            });

            requestAnimationFrame(animate);
        };

        animate();

        // Cleanup function
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseenter', handleMouseEnter);
            document.removeEventListener('mouseleave', handleMouseLeave);

            interactiveElements.forEach(el => {
                el.removeEventListener('mouseenter', handleHoverEnter);
                el.removeEventListener('mouseleave', handleHoverLeave);
            });

            if (dot && dot.parentNode) {
                dot.parentNode.removeChild(dot);
            }
            if (outline && outline.parentNode) {
                outline.parentNode.removeChild(outline);
            }
        };
    };

    onMounted(() => {
        // Only enable on desktop (not mobile/tablet)
        if (window.innerWidth > 1024) {
            const cleanup = initCursor();
            onUnmounted(cleanup);
        }
    });

    return {
        cursorDot,
        cursorOutline,
        isVisible
    };
}
